import { mergeAttributes, Node, nodePasteRule } from "@tiptap/core"

// Enhanced regex for Loom URLs to handle both share and embed URLs
export const LOOM_REGEX =
	/^(https?:\/\/)?(www\.)?loom\.com\/(share|embed)\/([a-zA-Z0-9-]+)(\?.*)?$/
export const LOOM_REGEX_GLOBAL = new RegExp(LOOM_REGEX, "g")

export const isValidLoomUrl = (url: string) => {
	return LOOM_REGEX.test(url.trim())
}

export const getEmbedUrlFromLoomUrl = ({
	url,
}: {
	url: string
	allowFullscreen?: boolean
}) => {
	url = url.trim()

	// If it's already an embed URL, strip allowfullscreen query param
	// (it's an iframe attribute, not a URL parameter, and breaks Loom's player)
	if (url.includes("loom.com/embed/")) {
		try {
			const parsed = new URL(url)
			parsed.searchParams.delete("allowfullscreen")
			return parsed.toString()
		} catch {
			return url
		}
	}

	const match = url.match(LOOM_REGEX)
	if (!match) return url

	const videoId = match[4]
	// Preserve query parameters if they exist
	const queryParams = match[5] || ""

	// Build the URL with any additional parameters needed
	const embedUrl = `https://www.loom.com/embed/${videoId}${queryParams}`

	return embedUrl
}

export interface LoomOptions {
	/**
	 * Controls if the paste handler for Loom videos should be added.
	 * @default true
	 */
	addPasteHandler: boolean

	/**
	 * Controls if the Loom video should be allowed to go fullscreen.
	 * @default true
	 */
	allowFullscreen: boolean

	/**
	 * The height of the Loom video.
	 * @default 480
	 */
	height: number | string

	/**
	 * The HTML attributes for a Loom video node.
	 * @default {}
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	HTMLAttributes: Record<string, any>

	/**
	 * Controls if the Loom node should be inline or not.
	 * @default false
	 */
	inline: boolean

	/**
	 * The width of the Loom video.
	 * @default 640
	 */
	width: number | string
}

/**
 * The options for setting a Loom video.
 */
interface SetLoomVideoOptions {
	src: string
	width?: number | string
	height?: number | string
}

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		loom: {
			/**
			 * Insert a Loom video
			 * @param options The Loom video attributes
			 * @example editor.commands.setLoomVideo({ src: 'https://www.loom.com/share/abcdef123456' })
			 */
			setLoomVideo: (options: SetLoomVideoOptions) => ReturnType
		}
	}
}

/**
 * This extension adds support for Loom videos.
 */
export const Loom = Node.create<LoomOptions>({
	name: "loom",

	addOptions() {
		return {
			addPasteHandler: true,
			allowFullscreen: true,
			height: 480,
			HTMLAttributes: {},
			inline: false,
			width: 640,
		}
	},

	inline() {
		return this.options.inline
	},

	group() {
		return this.options.inline ? "inline" : "block"
	},

	draggable: true,

	addAttributes() {
		return {
			src: {
				default: null,
			},
			width: {
				default: this.options.width,
			},
			height: {
				default: this.options.height,
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: "div[data-loom-video] iframe",
			},
		]
	},

	addCommands() {
		return {
			setLoomVideo:
				(options: SetLoomVideoOptions) =>
				({ commands }) => {
					if (!isValidLoomUrl(options.src)) {
						return false
					}

					return commands.insertContent({
						type: this.name,
						attrs: options,
					})
				},
		}
	},

	addPasteRules() {
		if (!this.options.addPasteHandler) {
			return []
		}

		return [
			nodePasteRule({
				find: LOOM_REGEX_GLOBAL,
				type: this.type,
				getAttributes: (match) => {
					return { src: match.input }
				},
			}),
		]
	},

	renderHTML({ HTMLAttributes }) {
		// Clone the attributes to avoid mutating the original
		const attrs = { ...HTMLAttributes }

		const embedUrl = getEmbedUrlFromLoomUrl({
			url: attrs.src as string,
			allowFullscreen: this.options.allowFullscreen,
		})

		// Safely assign the processed URL
		attrs.src = embedUrl

		return [
			"div",
			{ "data-loom-video": "" },
			[
				"iframe",
				mergeAttributes(
					this.options.HTMLAttributes,
					{
						width: this.options.width,
						height: this.options.height,
						allowfullscreen: this.options.allowFullscreen,
						allow: this.options.allowFullscreen ? "fullscreen" : undefined,
						frameborder: "0",
					},
					attrs,
				),
			],
		]
	},
})
